import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { useLang } from "../../context/LangContext";
import { scrollToId, EASE } from "../../lib/scroll";

const CLIPS = [
  { src: "https://images.unsplash.com/photo-1584217485941-319ab60e043b?crop=entropy&cs=srgb&fm=jpg&q=82&w=2400", pos: "center 50%" },
  { src: "https://images.unsplash.com/photo-1698760000113-6ff7c8d7b49a?crop=entropy&cs=srgb&fm=jpg&q=82&w=2400", pos: "center 35%" },
  { src: "https://images.unsplash.com/photo-1601368704635-fbcfc6349b93?crop=entropy&cs=srgb&fm=jpg&q=82&w=2400", pos: "center 45%" },
];
const SWITCH_MS = 9000;

/* Crisp, high-resolution stills with a slow cinematic push-in (Ken Burns) instead of compressed video */
const ImageLayer = ({ clip, active, index }) => (
  <motion.img
    src={clip.src}
    alt=""
    aria-hidden
    draggable={false}
    fetchpriority={index === 0 ? "high" : "low"}
    className="absolute inset-0 h-full w-full select-none object-cover"
    style={{ objectPosition: clip.pos }}
    initial={false}
    animate={{ opacity: active ? 1 : 0, scale: active ? [1, 1.12] : 1.12 }}
    transition={{ opacity: { duration: 1.8, ease: "easeInOut" }, scale: active ? { duration: SWITCH_MS / 1000 + 2, ease: "linear" } : { duration: 0 } }}
  />
);

export const Hero = ({ ready, onPrimary, onSecondary }) => {
  const { t, lang } = useLang();
  const h = t.hero;
  const ref = useRef(null);
  const [idx, setIdx] = useState(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, -160]);
  const opacity = useTransform(scrollYProgress, [0, 0.65], [1, 0]);
  const videoScale = useTransform(scrollYProgress, [0, 1], [1, 1.18]);
  const fade = (d) => ({ initial: { opacity: 0, y: 24 }, animate: ready ? { opacity: 1, y: 0 } : {}, transition: { duration: 1.1, ease: EASE, delay: d } });

  useEffect(() => {
    if (!ready) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % CLIPS.length), SWITCH_MS);
    return () => clearInterval(id);
  }, [ready]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="grain relative h-[100svh] min-h-[720px] overflow-hidden bg-[#0a0a0b] text-[#fafaf7]">
      <motion.div className="absolute inset-0" style={{ scale: videoScale }} initial={{ opacity: 0 }} animate={ready ? { opacity: 1 } : {}} transition={{ duration: 2.2, ease: EASE }}>
        {CLIPS.map((c, i) => <ImageLayer key={c.src} clip={c} index={i} active={ready && i === idx} />)}
      </motion.div>
      {/* cinematic veil */}
      <div className="pointer-events-none absolute inset-0 bg-[#0a0a0b]/25" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_50%_60%,transparent_30%,rgba(10,10,11,0.75)_100%)]" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-[#0a0a0b] via-[#0a0a0b]/60 to-transparent" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-[#0a0a0b]/80 to-transparent" />
      {/* golden eye signature */}
      <motion.img
        src="/logo-mark.png" alt="" aria-hidden draggable={false}
        className="pointer-events-none absolute right-[6%] top-[14%] hidden w-[24vw] max-w-[380px] select-none opacity-[0.22] mix-blend-screen md:block"
        initial={{ opacity: 0, scale: 0.9, filter: "blur(10px)" }}
        animate={ready ? { opacity: 0.22, scale: 1, filter: "blur(0px)" } : {}}
        transition={{ duration: 2.4, ease: EASE, delay: 0.6 }}
        data-testid="hero-eye-signature"
      />

      <motion.div style={{ y, opacity }} className="wrap relative z-10 flex h-full flex-col justify-end pb-20 md:pb-24">
        <motion.p {...fade(0.2)} className="eyebrow mb-8 flex items-center gap-3">
          <span className="h-px w-10 bg-[#d4af37]" /> {h.eyebrow}
        </motion.p>
        <h1 data-testid="hero-title" className="font-display text-[2.8rem] font-bold leading-[0.98] tracking-[-0.035em] sm:text-6xl lg:text-[6.4rem]">
          {h.lines.map((l, i) => (
            <span key={`${lang}-${i}`} className="block overflow-hidden pb-[0.06em]">
              <motion.span
                className="block"
                initial={{ y: "115%", rotate: 3 }}
                animate={ready ? { y: "0%", rotate: 0 } : {}}
                transition={{ duration: 1.25, ease: EASE, delay: 0.1 + i * 0.13 }}
              >
                {l.text}
                {l.accent && <span className="font-serif-i gold-text pr-2 font-normal">{l.accent}</span>}
              </motion.span>
            </span>
          ))}
        </h1>
        <div className="mt-10 grid gap-10 md:grid-cols-12 md:items-end">
          <motion.p {...fade(0.7)} data-testid="hero-subtitle" className="max-w-xl text-sm leading-relaxed text-white/80 sm:text-base md:col-span-6">
            {h.sub}
          </motion.p>
          <motion.div {...fade(0.85)} className="flex flex-wrap gap-4 md:col-span-6 md:justify-end">
            <button data-testid="hero-primary-cta" onClick={onPrimary} className="btn-gold group">
              {h.cta1} <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1" />
            </button>
            <button data-testid="hero-secondary-cta" onClick={onSecondary} className="btn-ghost">{h.cta2}</button>
          </motion.div>
        </div>
        <motion.div {...fade(1.1)} className="mt-14 flex items-center justify-between border-t border-white/10 pt-6 font-mono text-[11px] tracking-[0.25em] text-white/40">
          <span className="hidden sm:inline">{h.coords}</span>
          <div className="flex items-center gap-6" data-testid="hero-slide-indicator">
            {h.slides.map((s, i) => (
              <button key={s.k} onClick={() => setIdx(i)} className={`group flex items-center gap-3 uppercase transition-colors ${i === idx ? "text-[#e8c979]" : "hover:text-white/70"}`} data-testid={`hero-slide-${i}`}>
                <span>{s.k}</span>
                <span className="relative h-px w-12 overflow-hidden bg-white/15">
                  {i === idx && <motion.span key={`${i}-${idx}`} className="absolute inset-y-0 left-0 bg-[#d4af37]" initial={{ width: "0%" }} animate={{ width: "100%" }} transition={{ duration: SWITCH_MS / 1000, ease: "linear" }} />}
                </span>
                <span className="hidden lg:inline">{s.label}</span>
              </button>
            ))}
          </div>
          <button data-testid="hero-scroll-indicator" onClick={() => scrollToId("settori")} className="flex items-center gap-3 uppercase hover:text-[#e8c979]">
            {h.scroll}
            <motion.span animate={{ y: [0, 6, 0] }} transition={{ duration: 1.8, repeat: Infinity }}><ArrowDown className="h-4 w-4" /></motion.span>
          </button>
        </motion.div>
      </motion.div>
    </section>
  );
};
